import { Component, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { IModule } from '../setups/models/setup.interface';
import { ModuleService } from '../setups/services/module.service';
import { EMessageType } from '../../shared/data/response-default.interface';
import { ToastService } from '../../shared/services/toast.service';
import { ModalConfirmacaoService } from '../../shared/services/modal-confirmacao.service';
import { ERoleAdminString } from '../../shared/data/role.enum';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, filter, switchMap } from 'rxjs';
import { ModalDesativarModuloComponent } from './modal-desativar-modulo/modal-desativar-modulo.component';

@Component({
  selector: 'app-modules',
  templateUrl: './modules.component.html',
  styleUrls: ['./modules.component.scss']
})
export class ModulesComponent implements OnInit {
  ERoleAdminString = ERoleAdminString;
  modules: IModule[] = [];
  filteredModules: IModule[] = [];
  loading = false;

  searchControl = new UntypedFormControl('');
  statusControl = new UntypedFormControl('all');

  constructor(
    private readonly moduleService: ModuleService,
    private readonly toastService: ToastService,
    private readonly modalConfirmacao: ModalConfirmacaoService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.getModules();
    this.searchControl.valueChanges.subscribe(() => this.applyFilters());
    this.statusControl.valueChanges.subscribe(() => this.applyFilters());
  }

  getModules(): void {
    this.loading = true;
    this.moduleService.getAll(true).subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.isSucceed) {
          this.toastService.toast(response.messages || 'Erro ao buscar módulos.', EMessageType.Error);
          return;
        }

        this.modules = [...response.data].sort((a, b) => a.name.localeCompare(b.name));
        this.applyFilters();
      },
      error: () => {
        this.loading = false;
        this.toastService.toast('Erro ao buscar módulos.', EMessageType.Error);
      }
    });
  }

  toggleActive(module: IModule): void {
    const nextActive = !module.active;

    this.confirmToggle(module, nextActive)
      .pipe(
        filter(Boolean),
        switchMap(() => this.moduleService.setActive(module.id, nextActive))
      )
      .subscribe({
        next: (response) => {
          if (!response.isSucceed) {
            this.toastService.toast(response.messages || 'Erro ao atualizar módulo.', EMessageType.Error);
            return;
          }

          module.active = nextActive;
          this.applyFilters();
          this.toastService.toast(`Módulo ${nextActive ? 'ativado' : 'desativado'} com sucesso.`);
        },
        error: (error: HttpErrorResponse) =>
          this.toastService.toast(error.error?.messages || 'Erro ao atualizar módulo.', EMessageType.Error)
      });
  }

  // Desativar tira o módulo de todos os totens na hora, então mostra o impacto antes de aplicar.
  private confirmToggle(module: IModule, nextActive: boolean): Observable<boolean | undefined> {
    if (!nextActive) {
      return this.dialog
        .open(ModalDesativarModuloComponent, { data: { module }, width: '480px', panelClass: 'bg-iceberg' })
        .afterClosed();
    }

    return this.modalConfirmacao.abrirModalConfirmacao({
      mensagem: `Deseja ativar o módulo "${module.name}"?`,
      textoBotaoConfirma: 'Ativar',
      textoBotaoCancela: 'Cancelar'
    });
  }

  cleanFilters(): void {
    this.searchControl.setValue('');
    this.statusControl.setValue('all');
    this.applyFilters();
  }

  private applyFilters(): void {
    const search = String(this.searchControl.value || '')
      .trim()
      .toLowerCase();
    const status = this.statusControl.value;

    this.filteredModules = this.modules.filter((module) => {
      const matchesSearch =
        !search || module.name.toLowerCase().includes(search) || module.route.toLowerCase().includes(search);

      let matchesStatus = true;
      if (status === 'active') {
        matchesStatus = !!module.active;
      } else if (status === 'inactive') {
        matchesStatus = !module.active;
      }

      return matchesSearch && matchesStatus;
    });
  }
}
